import { ReactNode } from "react";
import {
  booleanFormatter,
  BooleanFormatterOptions,
  currencyFormatter,
  CurrencyFormatterOptions,
  dateFormatter,
  DateFormatterOptions,
  dateTimeFormatter,
  DateTimeFormatterOptions,
  htmlFormatter,
  markdownFormatter,
  numberFormatter,
  NumberFormatterOptions,
  percentFormatter,
  PercentFormatterOptions,
  timeFormatter,
  TimeFormatterOptions,
} from "./formatters";
import { ColumnType, ColumnTypeConfig, ColumnTypeOptionsMap } from "./types";

/**
 * Display defaults for a column type.
 * Defines the default formatter and alignment for each column type.
 */
export type ColumnTypeDefaults = {
  /**
   * Default horizontal alignment for the column.
   */
  align: "start" | "center" | "end";
  /**
   * Factory function to create the default renderCell function.
   * Receives formatter options and returns the renderCell function.
   */
  createRenderCell: (
    options?: ColumnTypeOptionsMap[ColumnType],
  ) => ((value: unknown, rowDepth: number) => ReactNode) | undefined;
};

/**
 * Mapping of column types to their display defaults.
 * Used by the column helper to apply sensible defaults based on data type.
 */
export const COLUMN_TYPE_DEFAULTS: Record<ColumnType, ColumnTypeDefaults> = {
  text: {
    align: "start",
    createRenderCell: () => undefined, // No formatter, use raw value
  },
  number: {
    align: "end",
    createRenderCell: (options) => (value) =>
      numberFormatter(
        value as number | undefined,
        options as NumberFormatterOptions,
      ),
  },
  currency: {
    align: "end",
    createRenderCell: (options) => (value) =>
      currencyFormatter(
        value as number | undefined,
        options as CurrencyFormatterOptions,
      ),
  },
  percent: {
    align: "end",
    createRenderCell: (options) => (value) =>
      percentFormatter(
        value as number | undefined,
        options as PercentFormatterOptions,
      ),
  },
  date: {
    align: "end",
    createRenderCell: (options) => (value) =>
      dateFormatter(
        value as string | undefined,
        options as DateFormatterOptions,
      ),
  },
  dateTime: {
    align: "end",
    createRenderCell: (options) => (value) =>
      dateTimeFormatter(
        value as string | Date | undefined,
        options as DateTimeFormatterOptions,
      ),
  },
  time: {
    align: "end",
    createRenderCell: (options) => (value) =>
      timeFormatter(
        value as string | undefined,
        options as TimeFormatterOptions,
      ),
  },
  boolean: {
    align: "start",
    createRenderCell: (options) => (value) =>
      booleanFormatter(
        value as boolean | undefined,
        options as BooleanFormatterOptions,
      ),
  },
  markdown: {
    align: "start",
    createRenderCell: () => (value) =>
      markdownFormatter(value as string | undefined),
  },
  html: {
    align: "start",
    createRenderCell: () => (value) =>
      htmlFormatter(value as string | undefined),
  },
};

/**
 * Extracts the column type string and options from a ColumnTypeConfig.
 *
 * @param config - The column type configuration (string or object)
 * @returns Tuple of [type string, options object or undefined]
 *
 * @example
 * ```ts
 * resolveColumnTypeConfig("currency")
 * // Returns: ["currency", undefined]
 *
 * resolveColumnTypeConfig({ type: "currency", options: { currency: "EUR" } })
 * // Returns: ["currency", { currency: "EUR" }]
 * ```
 */
export function resolveColumnTypeConfig<T extends ColumnType>(
  config: ColumnTypeConfig<T>,
): [T, ColumnTypeOptionsMap[T] | undefined] {
  if (typeof config === "string") {
    return [config, undefined];
  }
  return [config.type, config.options];
}

/**
 * Gets the display defaults for a column type.
 *
 * @param type - The column type
 * @returns The column type defaults (align and createRenderCell)
 *
 * @example
 * ```ts
 * const defaults = getColumnTypeDefaults("currency");
 * // Returns: { align: "end", createRenderCell: ... }
 * ```
 */
export function getColumnTypeDefaults(type: ColumnType): ColumnTypeDefaults {
  return COLUMN_TYPE_DEFAULTS[type];
}
